/* Catalog-backed recommendations. One shared vocabulary, strict eligibility, independent brand pools. */
(() => {
  if (window.NetlagerRelatedProducts) return;
  const responseCache = new Map();
  const CACHE_TTL = 120000;
  const tagKey = (value) =>
    String(value || "")
      .normalize("NFKC")
      .trim()
      .toLocaleLowerCase("da-DK")
      .replace(/\s+/g, " ");
  const vendorKey = (value) =>
    tagKey(value)
      .replace(/&/g, "and")
      .replace(/[^\p{L}\p{N}]/gu, "");
  const quote = (value) =>
    `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  const numericDiameter = (value) => {
    const match = String(value || "")
      .trim()
      .match(/^(?:Ø\s*)?(\d+(?:[.,]\d+)?)\s*(?:mm)?$/i);
    return match ? Number(match[1].replace(",", ".")) : 0;
  };
  function profile(product, rules) {
    const tags = new Set(
      (Array.isArray(product.tags) ? product.tags : []).map(tagKey),
    );
    // Specific whole tags are authoritative even when imported product_type/taxonomy is wrong.
    let type = rules.types.find((rule) => rule.key === product.category);
    if (!type)
      type = rules.types.find((rule) =>
        rule.tags.some((tag) => tags.has(tagKey(tag))),
      );
    if (!type) {
      const productType = tagKey(product.productType);
      type = rules.types.find((rule) =>
        rule.tags.some((tag) => tagKey(tag) === productType),
      );
    }
    if (!type) type = rules.types.find((rule) => rule.key === product.category);
    const hasGender = (gender) =>
      rules.genders[gender].some((tag) => tags.has(tagKey(tag)));
    let gender = "";
    if (hasGender("unisex") || (hasGender("women") && hasGender("men")))
      gender = "unisex";
    else if (hasGender("women")) gender = "women";
    else if (hasGender("men")) gender = "men";
    else {
      const value = tagKey(product.gender);
      gender =
        Object.keys(rules.genders).find((key) =>
          rules.genders[key].some((tag) => tagKey(tag) === value),
        ) || "";
    }
    return {
      tagged: tags.has(tagKey(rules.requiredTag)),
      type,
      gender,
      vendor: vendorKey(product.vendor),
      diameter: numericDiameter(product.diameter),
    };
  }
  function allowedVendors(value) {
    return String(value || "")
      .split(/[\n,]+/)
      .map((vendor) => vendor.trim())
      .filter(Boolean);
  }
  function searchQuery(current, rules, mode, allowlist = []) {
    const currentProfile = profile(current, rules);
    if (
      !currentProfile.tagged ||
      !currentProfile.type ||
      !currentProfile.vendor
    )
      return "";
    const typeTerms = currentProfile.type.tags.map(
      (tag) => `tag:${quote(tag)}`,
    );
    // Taxonomy/type fallback also permits correctly typed products without a defining tag.
    for (const alias of currentProfile.type.tags)
      typeTerms.push(`product_type:${quote(alias)}`);
    // Match the classifier's specific-before-broad tag precedence before fetching.
    // Imported product types such as Briller/Smykker must not broaden subtypes.
    const typeIndex = rules.types.indexOf(currentProfile.type);
    const excludedTags = rules.types.slice(0, typeIndex).flatMap((rule) => rule.tags);
    const brand = `${mode === "discovery" ? "NOT " : ""}vendor:${quote(String(current.vendor).trim())}`;
    let query = `tag:${quote(rules.requiredTag)} AND (${typeTerms.join(" OR ")}) AND ${brand}`;
    if (excludedTags.length)
      query += ` AND NOT (${excludedTags.map((tag) => `tag:${quote(tag)}`).join(" OR ")})`;
    if (currentProfile.gender === "women" || currentProfile.gender === "men") {
      const opposite = rules.genders[
        currentProfile.gender === "women" ? "men" : "women"
      ].map((tag) => `tag:${quote(tag)}`);
      const compatible = [
        ...rules.genders[currentProfile.gender],
        ...rules.genders.unisex,
      ].map((tag) => `tag:${quote(tag)}`);
      query += ` AND ((${compatible.join(" OR ")}) OR NOT (${opposite.join(" OR ")}))`;
    }
    if (allowlist.length)
      query += ` AND (${allowlist.map((vendor) => `vendor:${quote(vendor)}`).join(" OR ")})`;
    return query;
  }
  function selectProducts(
    products,
    current,
    rules,
    mode,
    limit,
    allowlist = [],
  ) {
    const source = profile(current, rules);
    if (!source.tagged || !source.type || !source.vendor) return [];
    const allowed = new Set(allowlist.map(vendorKey));
    const seen = new Set();
    const ranked = [];
    products.forEach((candidate, index) => {
      if (!candidate || typeof candidate !== "object") return;
      const target = profile(candidate, rules);
      const id = String(candidate.id);
      if (
        !candidate.id ||
        id === String(current.id) ||
        seen.has(id) ||
        candidate.available !== true ||
        !candidate.html
      )
        return;
      if (
        !target.tagged ||
        target.type?.key !== source.type.key ||
        !target.vendor
      )
        return;
      if (allowed.size && !allowed.has(target.vendor)) return;
      if ((mode === "same-brand") !== (target.vendor === source.vendor)) return;
      if (
        source.gender &&
        source.gender !== "unisex" &&
        target.gender &&
        target.gender !== "unisex" &&
        target.gender !== source.gender
      )
        return;
      seen.add(id);
      const genderRank =
        !source.gender ||
        source.gender === "unisex" ||
        target.gender === source.gender
          ? 0
          : target.gender === "unisex"
            ? 1
            : 2;
      const diameterRank =
        source.type.key === "WATCH" && source.diameter > 0
          ? target.diameter > 0
            ? Math.abs(source.diameter - target.diameter)
            : Infinity
          : 0;
      const priceRank =
        Number(current.price) > 0 && Number(candidate.price) > 0
          ? Math.abs(Number(current.price) - Number(candidate.price)) /
            Number(current.price)
          : 1;
      ranked.push({
        candidate,
        vendor: target.vendor,
        order: [genderRank, diameterRank, priceRank, index],
      });
    });
    ranked.sort((a, b) => {
      for (let index = 0; index < a.order.length; index++)
        if (a.order[index] !== b.order[index])
          return a.order[index] < b.order[index] ? -1 : 1;
      return 0;
    });
    if (mode === "same-brand")
      return ranked.slice(0, limit).map((item) => item.candidate);
    // First offer one strong match per brand, then fill remaining slots with the next best matches.
    const result = [];
    const brands = new Set();
    const selectedIds = new Set();
    for (const item of ranked) {
      if (brands.has(item.vendor)) continue;
      result.push(item.candidate);
      brands.add(item.vendor);
      selectedIds.add(String(item.candidate.id));
      if (result.length === limit) return result;
    }
    for (const item of ranked) {
      if (selectedIds.has(String(item.candidate.id))) continue;
      result.push(item.candidate);
      if (result.length === limit) break;
    }
    return result;
  }
  function announceProductCardsLoaded(wrapper) {
    wrapper.dispatchEvent(
      new CustomEvent("netlager:cards:loaded", { bubbles: true }),
    );
  }
  async function requestProducts(url, signal) {
    const key = `${url.href}|${document.documentElement.lang}|${window.Shopify?.currency?.active || ""}`;
    const cached = responseCache.get(key);
    if (cached && Date.now() - cached.time < CACHE_TTL) return cached.payload;
    const response = await fetch(url, {
      signal,
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!response.ok)
      throw new Error(`Related products request failed (${response.status})`);
    const payload = await response.json();
    if (!Array.isArray(payload.products))
      throw new Error("Invalid related products response");
    if (responseCache.size >= 40)
      responseCache.delete(responseCache.keys().next().value);
    responseCache.set(key, { time: Date.now(), payload });
    return payload;
  }
  async function load({
    root,
    signal,
    onRecommendationsReady,
    onDiscoveryReady,
  }) {
    const config = JSON.parse(
      root.querySelector("[data-related-config]").value,
    );
    const { current, rules } = config;
    const limit = Math.max(1, Math.min(8, Number(config.limit) || 6));
    const allowlist = allowedVendors(config.vendorAllowlist);
    const maxPages = Math.max(1, Math.min(2, Number(rules.maxPages) || 2));
    async function fill(mode, wrapperSelector, carouselSelector, ready) {
      const wrapper = root.querySelector(wrapperSelector);
      const carousel = wrapper?.querySelector(carouselSelector);
      const query = searchQuery(current, rules, mode, allowlist);
      if (!query || !wrapper || !carousel || signal.aborted) return [];
      const candidates = [];
      const visitedPages = new Set();
      let page = 1;
      let displayedIds = "";
      const renderSelected = (selected) => {
        if (!selected.length || signal.aborted || !root.isConnected) return;
        const ids = selected.map((candidate) => candidate.id).join(",");
        if (displayedIds === ids) return;
        displayedIds = ids;
        carousel.innerHTML = selected
          .map((candidate) => candidate.html)
          .join("");
        wrapper.hidden = false;
        wrapper.dataset.relatedState = "ready";
        announceProductCardsLoaded(wrapper);
        ready?.();
      };
      wrapper.dataset.relatedState = "loading";
      try {
        for (let request = 0; request < maxPages; request++) {
          if (signal.aborted || visitedPages.has(page)) return [];
          visitedPages.add(page);
          const url = new URL(
            root.dataset.searchUrl || "/search",
            window.location.origin,
          );
          if (url.origin !== window.location.origin)
            throw new Error("Related products must use the storefront origin");
          url.searchParams.set("view", "netlager-related");
          url.searchParams.set("type", "product");
          url.searchParams.set("options[unavailable_products]", "hide");
          url.searchParams.set("q", query);
          url.searchParams.set("page", String(page));
          const preview = new URL(window.location.href).searchParams.get(
            "preview_theme_id",
          );
          if (preview) url.searchParams.set("preview_theme_id", preview);
          const payload = await requestProducts(url, signal);
          candidates.push(
            ...payload.products.slice(0, Number(rules.pageSize) || 24),
          );
          const selected = selectProducts(
            candidates,
            current,
            rules,
            mode,
            limit,
            allowlist,
          );
          renderSelected(selected);
          // Brand diversity is a ranking preference, not a reason to delay a full row.
          const enough = selected.length >= limit;
          const nextPage = Number(payload.nextPage);
          if (enough || !Number.isInteger(nextPage) || nextPage <= page) break;
          page = nextPage;
        }
      } catch (error) {
        if (signal.aborted || error.name === "AbortError") return;
        wrapper.dataset.relatedError = error.message;
        // A later page failing must not discard valid matches from an earlier page.
      }
      if (signal.aborted || !root.isConnected) return [];
      const selected = selectProducts(
        candidates,
        current,
        rules,
        mode,
        limit,
        allowlist,
      );
      if (!selected.length) {
        wrapper.dataset.relatedState = wrapper.dataset.relatedError
          ? "error"
          : "empty";
        return [];
      }
      renderSelected(selected);
      return selected;
    }
    await Promise.allSettled([
      fill(
        "same-brand",
        "[data-recommendations-v33]",
        "[data-product-carousel-v33]",
        onRecommendationsReady,
      ),
      fill(
        "discovery",
        "[data-brand-discovery-loader-v33]",
        "[data-brand-product-carousel-v33]",
        onDiscoveryReady,
      ),
    ]);

    if (signal.aborted || !root.isConnected) return;
  }
  async function loadStandardRecommendations(scope = document) {
    const roots = [
      ...scope.querySelectorAll("[data-standard-recommendations]"),
    ];
    if (scope.matches?.("[data-standard-recommendations]"))
      roots.unshift(scope);
    await Promise.allSettled(
      roots.map(async (wrapper) => {
        if (wrapper.dataset.loaded) return;
        wrapper.dataset.loaded = "true";
        try {
          const url = new URL(wrapper.dataset.url, location.origin);
          const preview = new URL(location.href).searchParams.get(
            "preview_theme_id",
          );
          if (preview) url.searchParams.set("preview_theme_id", preview);
          const response = await fetch(url, { credentials: "same-origin" });
          if (!response.ok) throw new Error("Recommendations unavailable");
          const parsed = new DOMParser().parseFromString(
            await response.text(),
            "text/html",
          );
          const fresh = parsed.querySelector(
            "[data-standard-recommendation-cards]",
          );
          if (!wrapper.isConnected || !fresh?.querySelector("article")) return;
          wrapper
            .querySelector("[data-standard-recommendation-cards]")
            .replaceChildren(...fresh.childNodes);
          wrapper.hidden = false;
          announceProductCardsLoaded(wrapper);
        } catch (error) {
          wrapper.dataset.relatedError = error.message;
        }
      }),
    );
  }
  if (document.readyState === "loading")
    document.addEventListener(
      "DOMContentLoaded",
      () => loadStandardRecommendations(),
      { once: true },
    );
  else loadStandardRecommendations();
  document.addEventListener("shopify:section:load", (event) =>
    loadStandardRecommendations(event.target),
  );
  window.NetlagerRelatedProducts = {
    profile,
    searchQuery,
    selectProducts,
    load,
    vendorKey,
  };
})();
